import { motion } from 'framer-motion';
import { 
  ArrowRight,
  Target,
  Eye,
  Heart,
  Users,
  Award,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const About = ({ onDemoClick }) => {
  const values = [
    {
      icon: Target,
      title: 'Foco no Resultado',
      description: 'Trabalhamos orientados pelos resultados que nossos clientes precisam alcançar.'
    },
    {
      icon: Heart,
      title: 'Compromisso',
      description: 'Tratamos cada projeto como se fosse nosso, com dedicação e responsabilidade.'
    },
    {
      icon: Users,
      title: 'Parceria',
      description: 'Construímos relacionamentos de longo prazo baseados em confiança e transparência.'
    },
    {
      icon: TrendingUp,
      title: 'Inovação',
      description: 'Buscamos constantemente novas formas de entregar mais valor aos nossos clientes.'
    },
  ];

  const milestones = [
    { year: '2015', title: 'Fundação', description: 'Início das atividades focando em produção de conteúdo educacional.' },
    { year: '2017', title: 'Plataforma EAD', description: 'Lançamento da primeira versão da plataforma de ensino a distância.' },
    { year: '2019', title: 'Expansão', description: 'Abertura do estúdio próprio e expansão da equipe de produção.' },
    { year: '2021', title: '100 Clientes', description: 'Marco de 100 empresas utilizando nossas soluções.' },
    { year: '2023', title: 'NR-01', description: 'Lançamento da ferramenta de apoio para conformidade com NR-01.' },
    { year: '2024', title: '500+ Clientes', description: 'Mais de 500 empresas confiam na CSP para sua educação corporativa.' },
  ];

  const team = [
    {
      name: 'Nome do CEO',
      role: 'CEO & Fundador',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&facepad=2',
    },
    {
      name: 'Nome do CTO',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&facepad=2',
    },
    {
      name: 'Nome da COO',
      role: 'COO',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&facepad=2',
    },
    {
      name: 'Nome do Diretor',
      role: 'Diretor de Produção',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop&facepad=2',
    },
  ];

  const differentials = [
    'Equipe especializada em educação corporativa',
    'Estúdio próprio com equipamentos profissionais',
    'Suporte humano dedicado (não só bots)',
    'Soluções personalizadas para cada cliente',
    'Experiência em diversos segmentos de mercado',
    'Compromisso com prazos e qualidade',
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                Sobre a CSP
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                Transformando a{' '}
                <span className="text-blue-600">educação corporativa</span>{' '}
                desde 2015
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Somos especialistas em criar soluções completas para capacitação empresarial, 
                da plataforma à produção de conteúdo, ajudando empresas a desenvolver seus talentos 
                com qualidade e escala.
              </p>
              <Button
                onClick={onDemoClick}
                data-testid="about-hero-demo-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
              >
                Fale Conosco
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src="https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Equipe CSP"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full p-8 bg-blue-600 text-white border-0">
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-6">
                    <Target className="h-7 w-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-4">Nossa Missão</h2>
                  <p className="text-blue-100 text-lg">
                    Democratizar o acesso à educação corporativa de qualidade, fornecendo 
                    soluções tecnológicas e conteúdo profissional que transformam a forma 
                    como as empresas capacitam seus colaboradores.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full p-8 bg-orange-500 text-white border-0">
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-6">
                    <Eye className="h-7 w-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-4">Nossa Visão</h2>
                  <p className="text-orange-100 text-lg">
                    Ser a referência em soluções de educação corporativa no Brasil, 
                    reconhecida pela qualidade, inovação e pelos resultados que 
                    entregamos para nossos clientes.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nossos <span className="text-blue-600">Valores</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Os princípios que guiam nossas decisões e nosso trabalho diário.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full p-6 text-center border border-slate-100 hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-0">
                    <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="h-7 w-7 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nossa <span className="text-orange-500">Trajetória</span>
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-200" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-bold mb-2">
                      {milestone.year}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-900 mb-1">{milestone.title}</h3>
                    <p className="text-slate-600">{milestone.description}</p>
                  </div>

                  {/* Center dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-blue-600 border-4 border-white shadow" />

                  <div className="md:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nossa <span className="text-blue-600">Liderança</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Profissionais experientes comprometidos com o sucesso dos nossos clientes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative mb-4 inline-block">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-40 h-40 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{member.name}</h3>
                <p className="text-slate-600">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Por que escolher a{' '}
                <span className="text-orange-400">CSP</span>?
              </h2>
              <p className="text-slate-400 mb-8">
                Combinamos tecnologia, expertise em educação e atendimento humanizado 
                para entregar as melhores soluções de capacitação corporativa.
              </p>
              <ul className="space-y-4">
                {differentials.map((diff, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{diff}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <Award className="h-8 w-8 text-orange-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">10+</p>
                  <p className="text-slate-400 text-sm">Anos de Experiência</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6 text-center">
                <CardContent className="p-0">
                  <Users className="h-8 w-8 text-blue-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">30+</p>
                  <p className="text-slate-400 text-sm">Profissionais</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6 text-center col-span-2">
                <CardContent className="p-0">
                  <TrendingUp className="h-8 w-8 text-green-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">98%</p>
                  <p className="text-slate-400 text-sm">Taxa de Satisfação dos Clientes</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Vamos conversar sobre sua empresa?
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Entre em contato e descubra como podemos ajudar a transformar 
              a educação corporativa da sua organização.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="about-cta-demo-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Fale Conosco
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
